import React, { useState, useMemo } from 'react';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useBanks } from '../../hooks/useBanks';
import { PageHeader, StatsCards, FilterBar, ErrorState, type FilterConfig, type StatCard } from '../../components/Common';
import { useBanksReport, useDebouncedValue } from './useBanksReport';
import BankStatementSection from './BankStatementSection';

const BanksReport: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [bankFilter, setBankFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const debouncedSearch = useDebouncedValue(searchTerm);

  const banksQO = useMemo(() => ({ pagination: { pageSize: 100 } }), []);
  const { data: banksData = [] } = useBanks(banksQO);

  const reportFilters = useMemo(() => ({
    bank: bankFilter,
    type: typeFilter,
    dateFrom,
    dateTo,
    search: debouncedSearch,
  }), [bankFilter, typeFilter, dateFrom, dateTo, debouncedSearch]);

  const { summary, results, isLoading, error, refetch } = useBanksReport(reportFilters);

  const totalEntries = useMemo(
    () => results.reduce((sum: number, b: any) => sum + (b.entries?.length || 0), 0),
    [results]
  );

  const buildExportData = () => results.flatMap((block: any) =>
    (block.entries || []).map((e: any) => ({
      'Bank': block.bank?.name || '-',
      'Date': e.date ? new Date(e.date).toLocaleDateString('en-GB') : '-',
      'Direction': e.direction,
      'Source': e.source,
      'Amount': e.amount,
      'Currency': e.currency || block.bank?.currency || '-',
      'Description': e.description || '-',
      'Cheque No': e.chequeNumber || '-',
      'Reference': e.reference ? `${e.reference.type}: ${e.reference.description || e.reference.documentId}` : '-',
      'Balance Before': e.balanceBefore ?? '-',
      'Balance After': e.balanceAfter ?? '-',
    }))
  );

  const handleExport = () => exportToExcel(buildExportData(), `banks_statement_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(buildExportData(), 'Banks Statement Report');

  const handleClearFilters = () => {
    setSearchTerm('');
    setBankFilter('all');
    setTypeFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const stats: StatCard[] = [
    { label: 'Total Banks', value: summary?.totalBanks ?? 0, color: 'blue' },
    { label: 'Total Balance', value: (summary?.totalBalance ?? 0).toLocaleString(), color: 'purple' },
    { label: 'Total Credit (In)', value: (summary?.totalCredit ?? 0).toLocaleString(), color: 'green' },
    { label: 'Total Debit (Out)', value: (summary?.totalDebit ?? 0).toLocaleString(), color: 'red' },
  ];

  const filters: FilterConfig[] = [
    { key: 'bank', label: 'Bank', type: 'select', value: bankFilter, onChange: setBankFilter, options: [
      { value: 'all', label: 'All Banks' },
      ...(banksData as any[]).map((bank: any) => ({
        value: bank.documentId || String(bank.id),
        label: bank.name,
      })),
    ]},
    { key: 'type', label: 'Type', type: 'select', value: typeFilter, onChange: setTypeFilter, options: [
      { value: 'all', label: 'All Entries' },
      { value: 'credit', label: 'Credit (Money In)' },
      { value: 'debit', label: 'Debit (Money Out)' },
    ]},
    { key: 'dateFrom', label: 'Date From', type: 'date', value: dateFrom, onChange: setDateFrom },
    { key: 'dateTo', label: 'Date To', type: 'date', value: dateTo, onChange: setDateTo },
  ];

  if (error) return <ErrorState message={error} onRetry={refetch} entityName="banks report" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Banks Statement Report"
        description="Debit/credit statement per bank: transactions, bill payments and manual balance adjustments"
      />
      <StatsCards stats={stats} />
      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm}
        searchPlaceholder="Search description, cheque number, bill/invoice..."
        filters={filters} onClearFilters={handleClearFilters}
        totalCount={totalEntries} filteredCount={totalEntries} entityName="statement entries"
        onExport={handleExport} onPrint={handlePrint}
      />

      {isLoading ? <TableSkeleton /> : (
        <>
          {results.map((block: any) => (
            <BankStatementSection key={block.bank?.documentId || block.bank?.name} block={block} />
          ))}
          {results.length === 0 && (
            <div className="bg-white shadow rounded-lg text-center py-12">
              <p className="text-gray-500">No banks match the selected filters</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BanksReport;
